/**
 * WebSearchTask Component
 *
 * Information phase component for Teachers in the web-search condition.
 * Provides open web search with popup browser windows for natural browsing.
 *
 * Features:
 * - Search box with open web results via Serper API (no domain restrictions)
 * - Popup windows for viewing any web page (natural browser experience)
 * - Tracks all searches and page visits for research data
 * - Maintains heartbeat communication with popups
 *
 * Data Storage:
 * - player.set("webSearchData", { queries, pagesVisited, totalSearches, ... })
 */

import React, { useState, useEffect, useRef } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import {
  Search, ExternalLink, Loader2, AlertCircle,
  Globe, Clock, FileText, MousePointerClick,
  Target, MessageSquare, BookOpen
} from "lucide-react";

// Use relative URL - Caddy proxies /search to the Search API on port 5003
const SEARCH_API_URL = "";

export function WebSearchTask() {
  const player = usePlayer();
  const game = useGame();

  const config = game?.get("config");
  const topicName = config?.name || "the topic";
  const gameTopic = config?.gameTopic || config?.name || topicName;
  const instructionExplainer = config?.instructionExplainer;

  // Check game mode for appropriate messaging
  const gameMode = game?.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // Search state
  const [query, setQuery] = useState(gameTopic);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Popup tracking
  const [openPopups, setOpenPopups] = useState([]);
  const popupRefs = useRef(new Map());

  // Tracking state — initialized from the persisted record so a page refresh
  // or component remount does NOT wipe previously recorded queries/visits
  // (the save effect below runs on mount and would overwrite with empties)
  const [searchCount, setSearchCount] = useState(() => player.get("webSearchData")?.totalSearches || 0);
  const [queriesLog, setQueriesLog] = useState(() => player.get("webSearchData")?.queries || []);
  const [pagesVisited, setPagesVisited] = useState(() => player.get("webSearchData")?.pagesVisited || []);

  // Ref mirror of the latest tracking state for the unmount flush
  // (state setters don't apply during unmount cleanup)
  const trackingRef = useRef({ queriesLog: [], pagesVisited: [], searchCount: 0 });
  useEffect(() => {
    trackingRef.current = { queriesLog, pagesVisited, searchCount };
  }, [queriesLog, pagesVisited, searchCount]);

  // Update query when gameTopic loads (config may not be ready on first render)
  useEffect(() => {
    if (gameTopic && gameTopic !== "the topic") {
      setQuery(gameTopic);
    }
  }, [gameTopic]);

  // Record start time on mount
  useEffect(() => {
    if (!player.get("webSearchStartTime")) {
      player.set("webSearchStartTime", new Date().toISOString());
    }
  }, [player]);

  // Save tracking data whenever it changes
  useEffect(() => {
    player.set("webSearchData", {
      queries: queriesLog,
      pagesVisited,
      totalSearches: searchCount,
      totalPagesVisited: pagesVisited.length,
      lastUpdated: new Date().toISOString()
    });
  }, [queriesLog, pagesVisited, searchCount, player]);

  // Keep heartbeat alive by checking popups periodically
  // This ensures the main window stays "active" while user browses popups
  useEffect(() => {
    const checkPopups = setInterval(() => {
      // Clean up closed popups
      const stillOpen = [];
      openPopups.forEach(popup => {
        const win = popupRefs.current.get(popup.url);
        if (win && !win.closed) {
          stillOpen.push(popup);
        } else {
          // Popup was closed - log the time spent
          const timeSpent = Math.round((Date.now() - popup.openedAt) / 1000);
          setPagesVisited(prev =>
            prev.map(p => p.url === popup.url
              ? { ...p, closedAt: new Date().toISOString(), totalTime: timeSpent }
              : p
            )
          );
          popupRefs.current.delete(popup.url);
        }
      });

      if (stillOpen.length !== openPopups.length) {
        setOpenPopups(stillOpen);
      }

      // Touch the player to keep heartbeat fresh
      // (The Game.jsx heartbeat should handle this, but this is extra safety)
      if (stillOpen.length > 0) {
        player.set("lastPopupCheck", Date.now());
      }
    }, 5000);

    return () => clearInterval(checkPopups);
  }, [openPopups, player]);

  // Cleanup popups on unmount (phase end) and flush final dwell times.
  // Pages still open when the phase ends are typically the ones the
  // participant actually read — without this flush they exported totalTime: 0.
  useEffect(() => {
    return () => {
      popupRefs.current.forEach((win) => {
        if (win && !win.closed) {
          win.close();
        }
      });

      const closedAt = new Date().toISOString();
      const now = Date.now();
      const { queriesLog, pagesVisited, searchCount } = trackingRef.current;
      const finalPages = pagesVisited.map((p) =>
        p.closedAt
          ? p
          : {
              ...p,
              closedAt,
              totalTime: Math.max(0, Math.round((now - new Date(p.visitedAt).getTime()) / 1000)),
            }
      );

      // Persist directly — React state updates would not flush during unmount
      player.set("webSearchData", {
        queries: queriesLog,
        pagesVisited: finalPages,
        totalSearches: searchCount,
        totalPagesVisited: finalPages.length,
        lastUpdated: closedAt,
      });
    };
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    setHasSearched(true);

    try {
      const response = await fetch(`${SEARCH_API_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim(), num: 10 })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Search failed');
      }

      setResults(data.results || []);
      setSearchCount(prev => prev + 1);
      setQueriesLog(prev => [...prev, {
        query: query.trim(),
        timestamp: new Date().toISOString(),
        resultsCount: (data.results || []).length
      }]);

    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error.message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const openInPopup = (result) => {
    const url = result.link;

    // Check if already open
    const existingWin = popupRefs.current.get(url);
    if (existingWin && !existingWin.closed) {
      existingWin.focus();
      return;
    }

    // Calculate popup position (right side of screen)
    const width = 800;
    const height = 700;
    const left = window.screen.width - width - 50;
    const top = 100;

    // Open popup
    const popup = window.open(
      url,
      `research_${Date.now()}`,
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=yes,location=yes,status=yes,scrollbars=yes,resizable=yes`
    );

    if (popup) {
      popupRefs.current.set(url, popup);

      const newPopup = {
        url,
        title: result.title,
        openedAt: Date.now()
      };

      setOpenPopups(prev => [...prev, newPopup]);

      // Log page visit
      if (!pagesVisited.find(p => p.url === url)) {
        setPagesVisited(prev => [...prev, {
          url,
          title: result.title,
          displayLink: result.displayLink,
          visitedAt: new Date().toISOString(),
          totalTime: 0
        }]);
      }
    } else {
      // Popup blocked
      alert('Please allow popups for this site to view search results.');
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Parse learning objectives from instructionExplainer
  // Handles formats like "(1) first (2) second" or "1. first 2. second"
  const parseLearningObjectives = (text) => {
    if (!text) return [];

    // Try to extract numbered items like (1), (2) or 1., 2.
    const pattern = /\((\d+)\)\s*([^(]+?)(?=\(\d+\)|$)/g;
    const matches = [...text.matchAll(pattern)];

    if (matches.length > 0) {
      return matches.map(m => m[2].trim().replace(/,\s*$/, ''));
    }

    // Fallback: return the whole text as a single item
    return [text];
  };

  const learningObjectives = parseLearningObjectives(instructionExplainer);

  return (
    <div className="flex flex-col h-full w-full max-w-5xl mx-auto p-4 gap-3">
      {/* Header with Topic */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 text-white shadow-lg flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">{topicName}</h1>
            <p className="text-purple-200 text-sm">Search the web to learn about this topic</p>
          </div>
        </div>
      </div>

      {/* Context & Learning Objectives - Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-shrink-0">
        {/* What's Next Context */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4" />
            What happens next
          </h3>
          <ol className="text-xs text-blue-700 space-y-1.5">
            {isSingleMode ? (
              <>
                <li className="flex gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                  <span>You'll <strong>review what you learned</strong> and reflect on key points</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                  <span>You'll <strong>take a quiz</strong> to test your understanding</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                  <span>Learn thoroughly so you can answer questions!</span>
                </li>
              </>
            ) : (
              <>
                <li className="flex gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                  <span>You'll <strong>chat with your partner</strong> to explain what you learned</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                  <span>Your partner will <strong>answer quiz questions</strong> based on your explanation</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                  <span>Learn thoroughly so you can teach it clearly!</span>
                </li>
              </>
            )}
          </ol>
        </div>

        {/* Learning Objectives */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <h3 className="text-sm font-semibold text-emerald-800 flex items-center gap-2 mb-2">
            <Target className="w-4 h-4" />
            What to learn
          </h3>
          {learningObjectives.length > 1 ? (
            <ul className="text-xs text-emerald-700 space-y-1">
              {learningObjectives.map((objective, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="text-emerald-500 flex-shrink-0">•</span>
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-emerald-700">
              {instructionExplainer || (isSingleMode
                ? "Research this topic thoroughly so you can answer questions about it."
                : "Research this topic thoroughly so you can explain it to your partner.")}
            </p>
          )}
        </div>
      </div>

      {/* Search Box */}
      <form onSubmit={handleSearch} className="flex gap-2 flex-shrink-0">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the web..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
          />
        </div>
        <button
          type="submit"
          disabled={isSearching || !query.trim()}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {isSearching ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
          Search
        </button>
      </form>

      {/* Stats Bar */}
      <div className="flex items-center justify-between text-sm flex-shrink-0">
        <div className="flex items-center gap-4 text-gray-500">
          <span className="flex items-center gap-1">
            <Search className="w-4 h-4" />
            {searchCount} search{searchCount !== 1 ? 'es' : ''}
          </span>
          <span className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            {pagesVisited.length} page{pagesVisited.length !== 1 ? 's' : ''} visited
          </span>
          {openPopups.length > 0 && (
            <span className="flex items-center gap-1 text-purple-600">
              <ExternalLink className="w-4 h-4" />
              {openPopups.length} window{openPopups.length !== 1 ? 's' : ''} open
            </span>
          )}
        </div>
        {/* Gentle reminder to keep window visible */}
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Globe className="w-3 h-3" />
          This is your research hub - keep it open
        </span>
      </div>

      {/* Error Message */}
      {searchError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex-shrink-0">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{searchError}</span>
        </div>
      )}

      {/* Results List */}
      <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
        {results.map((result, index) => {
          const isOpen = openPopups.some(p => p.url === result.link);
          const wasVisited = pagesVisited.some(p => p.url === result.link);

          return (
            <div
              key={index}
              className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                isOpen ? 'border-purple-400 bg-purple-50' : wasVisited ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
              }`}
              onClick={() => openInPopup(result)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-purple-700 hover:text-purple-900 line-clamp-2">
                    {result.title}
                  </h3>
                  <p className="text-xs text-green-700 mt-0.5 truncate flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {result.displayLink}
                  </p>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{result.snippet}</p>
                </div>
                <div className="flex-shrink-0">
                  {isOpen ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      <ExternalLink className="w-3 h-3" />
                      Open
                    </span>
                  ) : wasVisited ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <Clock className="w-3 h-3" />
                      Visited
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                      <MousePointerClick className="w-3 h-3" />
                      Click to open
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {!hasSearched && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-3">
              <Search className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-base font-medium">Ready to research</p>
            <p className="text-sm mt-1 text-gray-400">Click Search to find web pages</p>
          </div>
        )}

        {hasSearched && results.length === 0 && !isSearching && !searchError && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <AlertCircle className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-base font-medium">No results found</p>
            <p className="text-sm text-gray-400">Try different search terms</p>
          </div>
        )}
      </div>

      {/* Visited Pages Summary */}
      {pagesVisited.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex-shrink-0">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Your research ({pagesVisited.length} page{pagesVisited.length !== 1 ? 's' : ''})
          </h4>
          <div className="flex flex-wrap gap-2">
            {pagesVisited.map((page, idx) => (
              <button
                key={idx}
                onClick={() => openInPopup({ link: page.url, title: page.title, displayLink: page.displayLink })}
                className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-gray-100 truncate max-w-[200px] flex items-center gap-1"
                title={page.title}
              >
                <Globe className="w-3 h-3 text-gray-400 flex-shrink-0" />
                {page.displayLink}
                {page.totalTime > 0 && (
                  <span className="text-gray-400 ml-1">({formatTime(page.totalTime)})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
